import React, { useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { Home, FileText, History, LogOut, CalendarRange } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import InstallApp from "@/components/InstallApp";
import DriverOperationalNotice from "@/components/DriverOperationalNotice";
import DriverGuidedTour from "@/components/DriverGuidedTour";

export default function DriverLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", "driver");
    return () => document.documentElement.removeAttribute("data-theme");
  }, []);

  function onLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  return (
    <div className="min-h-screen bg-[#0A0E17] text-slate-100" data-theme="driver">
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-slate-800/80 bg-[#0A0E17]/90 backdrop-blur">
        <div className="mx-auto flex max-w-2xl items-center justify-between px-4 py-3">
          <div className="leading-tight">
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-blue-400">
              D1 · Motorista
            </p>
            <p className="font-display text-sm font-semibold text-slate-100" data-testid="driver-greeting-name">
              {user?.nome}
            </p>
          </div>
          <button
            onClick={onLogout}
            className="rounded-lg border border-slate-800 px-3 py-2 text-xs font-medium text-slate-300 hover:border-slate-600 hover:text-white"
            data-testid="driver-logout-btn"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 pb-28 pt-6">
        <InstallApp />
        <DriverOperationalNotice />
        <Outlet />
      </main>

      <DriverGuidedTour />

      {/* Bottom nav */}
      <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-800/80 bg-[#0A0E17]/95 backdrop-blur">
        <div className="mx-auto grid max-w-2xl grid-cols-5">
          {[
            { to: "/motorista", icon: Home, label: "Início", end: true, testId: "nav-driver-home" },
            { to: "/motorista/fechamento", icon: FileText, label: "Fechamento", testId: "nav-driver-closing" },
            { to: "/motorista/historico", icon: History, label: "Histórico", testId: "nav-driver-history" },
            { to: "/motorista/ocorrencias", icon: FileText, label: "Ocorrências", testId: "nav-driver-occurrences" },
            { to: "/motorista/mensal", icon: CalendarRange, label: "Mensal", testId: "nav-driver-monthly" },
          ].map(({ to, icon: Icon, label, end, testId }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testId}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-3 text-[11px] font-medium transition ${
                  isActive ? "text-blue-400" : "text-slate-500 hover:text-slate-300"
                }`
              }
            >
              <Icon className="h-5 w-5" />
              {label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
